/**
 * Download the rows on screen as a file: `downloadCsv` or `downloadXlsx`.
 *
 * Both take the header row and the body rows separately, with the cells already formatted
 * the way the table draws them, and a filename **without** its extension. `downloadXlsx`
 * fetches the sheet writer on demand, so it returns a promise; neither reports anything to
 * the user, since the browser's own download is the feedback.
 */

/** A cell as the table renders it: already formatted, or a raw value to stringify. */
export type ExportCell = string | number | null | undefined

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Revoking straight away cancels the download in Safari, so it waits a tick.
  setTimeout(() => URL.revokeObjectURL(url))
}

/**
 * The characters Excel and Sheets read as the start of a formula. Quoting does not stop
 * them: a quoted `=HYPERLINK(...)` is still evaluated on open, and the sheet then runs
 * whatever a publisher typed into a comment or a spot name.
 */
const FORMULA_START = /^[=+\-@\t\r]/

/**
 * RFC 4180: quote every field, and double the quotes inside one.
 *
 * A **string** that opens with a formula character is also prefixed with an apostrophe,
 * which Excel eats and treats as "this cell is text". A `number` never is, so an amount
 * column still sums — that is why `ExportCell` keeps numbers apart from the strings a
 * column formatted itself, and why this branches on the type rather than on the text. The
 * cost is that a *formatted* negative (`-$ 1,234.00`, a string) opens as text, which it
 * already was: nothing sums a column carrying a currency mark.
 *
 * `downloadXlsx` needs none of this — SheetJS writes a string as a string cell, and a
 * formula only exists where a cell is given one explicitly.
 */
function csvField(value: ExportCell): string {
  const text = String(value ?? '')
  const guarded =
    typeof value !== 'number' && FORMULA_START.test(text) ? `'${text}` : text

  return `"${guarded.replace(/"/g, '""')}"`
}

export function downloadCsv(
  headers: string[],
  rows: ExportCell[][],
  filename = 'export',
  separator = ','
) {
  const lines = [headers, ...rows].map((row) =>
    row.map(csvField).join(separator)
  )

  // The BOM is what makes Excel open a UTF-8 CSV as UTF-8 rather than as Latin-1.
  const blob = new Blob(['﻿', lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;'
  })

  saveBlob(blob, `${filename}.csv`)
}

export async function downloadXlsx(
  headers: string[],
  rows: ExportCell[][],
  filename = 'export'
) {
  // Loaded on demand: the sheet writer is far larger than the page that offers the button,
  // and most visits never press it.
  const XLSX = await import('xlsx')

  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
  const book = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(book, sheet, 'Report')

  const out = XLSX.write(book, {
    bookType: 'xlsx',
    type: 'array'
  }) as ArrayBuffer

  saveBlob(
    new Blob([out], { type: 'application/octet-stream' }),
    `${filename}.xlsx`
  )
}
